import type { ChangeEvent, JSX } from "react";
import type { App } from "../app";
import type { Attribute } from "../sign-up-request";
import { Add, Remove } from "@mui/icons-material";
import { IconButton, Stack, TextField, Typography } from "@mui/material";
import { bound, useObservable } from "ecce-react";
import { SignUpStep } from "../step";


export class CollectQuantityStep extends SignUpStep {
	override readonly title = "Quantity";
	override view: () => JSX.Element = () => <QuantityView step={ this }/>;

	readonly attribute: Attribute<number | null>;

	constructor(app: App) {
		super(app);
		this.attribute = app.state.attribute("quantity");
	}

	get quantity(): number | null {
		return this.attribute.value;
	}

	#setValue(value: number): void {
		if(Number.isNaN(value)) {
			this.attribute.value = null;
			return;
		}

		this.attribute.value = Math.max(this.min, Math.min(1000, value));
	}

	@bound()
	handleChange(ev: ChangeEvent<HTMLInputElement>): void {
		this.#setValue(parseInt(ev.target.value, 10));
	}

	@bound()
	increment(): void {
		this.#setValue((this.quantity ?? 0) + 1);
	}

	@bound()
	decrement(): void {
		this.#setValue((this.quantity ?? 2) - 1);
	}

	@bound()
	handleBlur(): void {
		this.attribute.value ??= 1;
	}

	protected override handleSubmit(): Promise<void> {
		this.app.onQuantitySelected();
		return Promise.resolve();
	}

	get isMonthly(): boolean {
		return this.app.data?.paymentCycle === "monthly";
	}

	get min(): number {
		return this.isMonthly ? 3 : 1;
	}

	get annualUrl(): string {
		return "#" + this.app.data?.hash?.replace("monthly", "annual");
	}
}

const QuantityView = (p: { step: CollectQuantityStep; }): JSX.Element => {
	useObservable(p.step.attribute);

	return (
		<>
			<Typography>How many listings do you require?</Typography>
			<Stack marginY="auto" gap={ 2 }>
				<TextField
					label="Number of Listings"
					value={ p.step.quantity?.toString() ?? "" }
					required
					onChange={ p.step.handleChange }
					onBlur={ p.step.handleBlur }
					slotProps={{
						input: {
							slotProps: {
								input: {
									min: p.step.min,
								},
							},
							sx: {
								"input": { textAlign: "center" },
							},
							startAdornment: (
								<IconButton onClick={ p.step.decrement }>
									<Remove/>
								</IconButton>
							),
							endAdornment: (
								<IconButton onClick={ p.step.increment }>
									<Add/>
								</IconButton>
							),
						},
					}}
				/>
				{ p.step.isMonthly && (
					<>
						<Typography variant="body2">
							Monthly listings are only provided on a 12-month term to Central Agents with a minimum of 3 yachts.
						</Typography>
						<Typography variant="body2">
							Select an <a href={ p.step.annualUrl }>annual payment plan</a> to purchase less than 3 yacht listings.
						</Typography>
					</>
				) }
			</Stack>
		</>
	);
};
